import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Clock, MessageSquare } from 'lucide-react';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useChatStore } from '../../stores/useChatStore.js';

interface SearchTasksPanelProps {
  isOpen: boolean;
  onClose: () => void;
}



const SearchTasksPanel: React.FC<SearchTasksPanelProps> = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebounce(searchQuery, 200);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { sessions, setSessionId, loadSession } = useChatStore();

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const searchResults = React.useMemo(() => {
    if (!debouncedQuery.trim()) {
      return sessions.slice(0, 10).map(session => ({
        id: session.id,
        name: session.name,
        lastMessage: '',
        messageCount: 0,
        createdAt: session.createdAt
      }));
    }

    const query = debouncedQuery.toLowerCase();
    return sessions
      .filter(session => {
        const nameMatch = session.name.toLowerCase().includes(query);
        
        try {
          const sessionData = localStorage.getItem(`beehive-session-${session.id}`);
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            const messageMatch = parsed.messages?.some((msg: { content: string }) => 
              msg.content.toLowerCase().includes(query)
            );
            return nameMatch || messageMatch;
          }
        } catch (error) {
          console.warn('Error parsing session data:', error);
        }
        
        return nameMatch;
      })
      .slice(0, 10)
      .map(session => {
        let lastMessage = '';
        let messageCount = 0;
        
        try {
          const sessionData = localStorage.getItem(`beehive-session-${session.id}`);
          if (sessionData) {
            const parsed = JSON.parse(sessionData);
            messageCount = parsed.messages?.length || 0;
            const lastMsg = parsed.messages?.[parsed.messages.length - 1];
            if (lastMsg) {
              lastMessage = lastMsg.content.length > 60 
                ? lastMsg.content.substring(0, 60) + '...'
                : lastMsg.content;
            }
          }
        } catch (error) {
          console.warn('Error parsing session data:', error);
        }
        
        return {
          id: session.id,
          name: session.name,
          lastMessage,
          messageCount,
          createdAt: session.createdAt
        };
      });
  }, [debouncedQuery, sessions]);

  const handleSessionSelect = async (sessionId: string) => {
    try {
      await loadSession(sessionId);
      setSessionId(sessionId);
      onClose();
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && searchResults.length > 0) {
      handleSessionSelect(searchResults[0].id);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed top-16 right-4 w-96 max-w-[calc(100vw-2rem)] glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300 shadow-xl overflow-hidden z-50"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="p-3 border-b border-slate-700">
              <div className="flex items-center space-x-2">
                <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search tasks and sessions..."
                  className="flex-1 bg-transparent border-none text-white placeholder-slate-400 focus:outline-none text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            <div className="max-h-80 overflow-y-auto">
              {searchResults.length > 0 ? (
                <div className="py-2">
                  {searchResults.map((session, index) => (
                    <motion.button
                      key={session.id}
                      onClick={() => handleSessionSelect(session.id)}
                      className="w-full text-left p-3 hover:bg-slate-700/50 transition-colors border-b border-slate-700/50 last:border-0 group"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 4 }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <div className="text-sm font-medium text-white truncate group-hover:text-violet-300 transition-colors">
                              {session.name}
                            </div>
                            {session.messageCount > 0 && (
                              <div className="flex items-center space-x-1 text-xs text-slate-400">
                                <MessageSquare className="w-3 h-3" />
                                <span>{session.messageCount}</span>
                              </div>
                            )}
                          </div>
                          {session.lastMessage && (
                            <div className="text-xs text-slate-400 truncate">
                              {session.lastMessage}
                            </div>
                          )}
                          {session.createdAt && (
                            <div className="flex items-center space-x-1 text-xs text-slate-500 mt-1">
                              <Clock className="w-3 h-3" />
                              <span>{new Date(session.createdAt).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <Search className="w-8 h-8 text-slate-500 mx-auto mb-2" />
                  <div className="text-sm text-slate-400">
                    {debouncedQuery ? 'No matching tasks found' : 'Type to search tasks and sessions'}
                  </div>
                  {!debouncedQuery && (
                    <div className="text-xs text-slate-500 mt-1">
                      Press Enter to select first result, Esc to close
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SearchTasksPanel;
